/**
 * Fuentes del editor 2D.
 *
 * Las declara este módulo con la FontFace API en vez de un bloque `@font-face`
 * en CSS, por dos razones:
 *
 * 1. **El canvas no dispara la descarga.** Un `@font-face` solo se descarga
 *    cuando algo del DOM usa la familia, y fabric dibuja el texto en un
 *    `<canvas>`, que no cuenta. Al abrir un diseño por primera vez el `.ttf`
 *    todavía no estaba, así que fabric medía y pintaba la fuente de reserva;
 *    solo al abrir el panel de texto (las tarjetas de fuente sí son DOM) se
 *    descargaba, y de ahí que las letras salieran bien "yendo adelante y
 *    atrás". Con las familias declaradas aquí se pueden pedir explícitamente
 *    (`ensureFontsLoaded`) y volver a medir/repintar (`refreshCanvasFonts`).
 * 2. **Una sola lista.** Antes había una en CSS y otra en el panel de texto:
 *    "Rock Salt" llevaba tiempo sin renderizar porque el `@font-face` la
 *    declaraba como `RockSalt`.
 *
 * Añadir una fuente = una línea aquí + el `.ttf` en `public/fonts/`.
 */

/** Nombre de la familia → nombre del fichero en `public/fonts/` (sin `.ttf`). */
export const EDITOR_FONTS = {
    "Abril Fatface": "AbrilFatface-Regular",
    "Alfa Slab One": "AlfaSlabOne-Regular",
    "Anton": "Anton-Regular",
    "Arizonia": "Arizonia-Regular",
    "Bangers": "Bangers-Regular",
    "Bebas Neue": "BebasNeue-Regular",
    "Blaka": "Blaka-Regular",
    "Blaka Hollow": "BlakaHollow-Regular",
    "Goblin One": "GoblinOne-Regular",
    "Henny Penny": "HennyPenny-Regular",
    "Holtwood OneSC": "HoltwoodOneSC-Regular",
    "Kristi": "Kristi-Regular",
    "Lobster": "Lobster-Regular",
    "Luckiest Guy": "LuckiestGuy-Regular",
    "Merienda One": "MeriendaOne-Regular",
    "Mochiy Pop One": "MochiyPopOne-Regular",
    "Mr Dafoe": "MrDafoe-Regular",
    "Pacifico": "Pacifico-Regular",
    "Permanent Marker": "PermanentMarker-Regular",
    "Pirata One": "PirataOne-Regular",
    "Questrial": "Questrial-Regular",
    "Rampart One": "RampartOne-Regular",
    "Righteous": "Righteous-Regular",
    "Roboto": "Roboto-Regular",
    "Rock Salt": "RockSalt-Regular",
    "Rye": "Rye-Regular",
    "Saira Stencil One": "SairaStencilOne-Regular",
    "Seaweed Script": "SeaweedScript-Regular",
    "Secular One": "SecularOne-Regular",
    "Sniglet": "Sniglet-Regular",
    "Ultra": "Ultra-Regular",
    "Yesteryear": "Yesteryear-Regular",
}

export const DEFAULT_EDITOR_FONT = "Abril Fatface"

const FONT_DIR = "/fonts"
const TEXT_TYPES = new Set(["text", "i-text", "textbox"])

/** family → FontFace ya registrada en el documento. */
const registered = new Map()
/** family → promesa de carga (memoizada: un `.ttf` se pide una vez). */
const loading = new Map()

const fontsAvailable = () => typeof document !== "undefined" && !!document.fonts

// Un SVG importado trae la familia entrecomillada ("'Bebas Neue'"): sin quitar
// las comillas no coincidiría con nada del registro.
const normalizeFamily = (family) => String(family || "").trim().replace(/^['"]|['"]$/g, "")

/** Familia entrecomillada si el nombre no es un identificador CSS. */
const cssFamily = (family) => (/^[\w-]+$/.test(family) ? family : `"${family}"`)

/**
 * Registra las familias del editor en el documento. Solo las declara: el `.ttf`
 * se descarga cuando alguien lo pide (`ensureFontsLoaded`) o cuando el DOM usa
 * la familia, igual que con un `@font-face`.
 */
const registerEditorFonts = () => {
    if (!fontsAvailable() || typeof FontFace === "undefined") return
    Object.entries(EDITOR_FONTS).forEach(([family, file]) => {
        if (registered.has(family)) return
        try {
            const face = new FontFace(family, `url(${FONT_DIR}/${file}.ttf) format("truetype")`, {
                display: "swap",
            })
            document.fonts.add(face)
            registered.set(family, face)
        }
        catch (error) {
            console.warn(`[fonts] no se pudo registrar "${family}"`, error)
        }
    })
}

registerEditorFonts()

const loadFamily = (rawFamily) => {
    const family = normalizeFamily(rawFamily)
    if (!family) return Promise.resolve()
    if (loading.has(family)) return loading.get(family)

    const face = registered.get(family)
    // Familias que no son del editor (las que puede traer un SVG subido):
    // `document.fonts.load` resuelve sin más si nada coincide.
    const promise = (face ? face.load() : document.fonts.load(`16px ${cssFamily(family)}`))
        .catch((error) => {
            // Una fuente que no llega no debe bloquear el lienzo: se pinta con
            // la de reserva, como hasta ahora.
            console.warn(`[fonts] no se pudo cargar "${family}"`, error)
        })

    loading.set(family, promise)
    return promise
}

/** ¿Está ya el fichero de la familia en memoria? */
export const isFontLoaded = (rawFamily) => {
    const family = normalizeFamily(rawFamily)
    if (!family || !fontsAvailable()) return true
    try {
        return document.fonts.check(`16px ${cssFamily(family)}`)
    }
    catch {
        return false
    }
}

/**
 * Descarga las familias indicadas. Nunca rechaza y la promesa de cada familia
 * se reutiliza, así que llamarla de más es gratis.
 */
export const ensureFontsLoaded = (families) => {
    const wanted = [...new Set((families || []).filter(Boolean))]
    if (!wanted.length || !fontsAvailable()) return Promise.resolve()
    return Promise.all(wanted.map(loadFamily)).then(() => undefined)
}

/** Objetos de texto del canvas, incluidos los que están dentro de grupos. */
export const collectTextObjects = (canvas) => {
    const found = []
    const walk = (object) => {
        if (!object) return
        if (TEXT_TYPES.has(object.type)) found.push(object)
        if (object._objects) object._objects.forEach(walk)
    }
    canvas.getObjects().forEach(walk)
    return found
}

/** Familias usadas por un diseño serializado (string u objeto). */
export const collectFontFamiliesFromJSON = (json) => {
    let data = json
    if (typeof data === "string") {
        try {
            data = JSON.parse(data)
        }
        catch {
            return []
        }
    }

    const families = new Set()
    const walk = (node) => {
        if (!node || typeof node !== "object") return
        if (Array.isArray(node)) {
            node.forEach(walk)
            return
        }
        if (typeof node.fontFamily === "string") families.add(node.fontFamily)
        if (Array.isArray(node.objects)) node.objects.forEach(walk)
    }
    walk(data)
    return [...families]
}

/** El canvas puede haberse destruido mientras esperábamos las fuentes. */
const canvasAlive = (canvas) => !!canvas && !!canvas.lowerCanvasEl

/** Marca el objeto y sus grupos padre para que se repinte la caché. */
const markDirty = (object) => {
    let node = object
    while (node) {
        node.dirty = true
        node = node.group
    }
}

/**
 * Espera las fuentes que usa el canvas y vuelve a medir y repintar el texto.
 *
 * Volver a medir es tan importante como repintar: el ancho y el alto que fabric
 * tiene cacheados se calcularon con la fuente de reserva, así que sin
 * `initDimensions()` el texto se ve bien pero la caja de selección — y el SVG
 * que se manda al backend — siguen con las medidas equivocadas.
 */
export const refreshCanvasFonts = async (canvas) => {
    if (!canvasAlive(canvas)) return

    const texts = collectTextObjects(canvas)
    if (!texts.length) return

    await ensureFontsLoaded(texts.map((text) => text.fontFamily))
    if (!canvasAlive(canvas)) return

    texts.forEach((text) => {
        // Dentro de un grupo no se re-mide: cambiarle las dimensiones a un hijo
        // deforma el grupo. Basta con invalidar la caché para que se repinte.
        if (!text.group && typeof text.initDimensions === "function") {
            text.initDimensions()
            text.setCoords()
        }
        markDirty(text)
    })

    canvas.requestRenderAll()
}
